import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils/cn";
import { Pin, Zap } from "lucide-react";

export interface ConfigParamRowProps {
  label: string;
  description?: string;
  value: number | [number, number] | boolean;
  isPinned?: boolean;
  isOptimizing?: boolean; // For booleans: true = will test both states
  type: "range" | "boolean" | "number";
}

/**
 * ConfigParamRow - Display a single parameter row in the study config summary
 *
 * Supports three parameter types:
 * - range: Shows [min, max] or single value with Pin icon if pinned
 * - number: Shows a single numeric value
 * - boolean: Shows OFF/Optimize/ON states with appropriate styling
 *
 * Visual states:
 * - Boolean OFF: Gray background, "Off" badge
 * - Boolean Optimize: Amber badge with Zap icon, "Optimize" text
 * - Boolean ON: Emerald/green background, "On" badge
 * - Range/Number pinned: Amber styling with Pin icon
 */
export function ConfigParamRow({
  label,
  description,
  value,
  isPinned = false,
  isOptimizing = false,
  type,
}: ConfigParamRowProps) {
  const content = (
    <div
      className={cn(
        "flex items-center justify-between p-3 rounded-lg border transition-colors",
        // Boolean styling
        type === "boolean" && value === true && !isOptimizing
          ? "bg-emerald-500/5 border-emerald-500/20 hover:bg-emerald-500/10"
          : "bg-white/[0.02] border-white/5 hover:bg-white/[0.04]"
      )}
    >
      <div className="flex items-center gap-2">
        {/* Show Pin icon for pinned range/number values */}
        {(type === "range" || type === "number") && isPinned && (
          <Pin className="h-3.5 w-3.5 text-amber-400" />
        )}
        {/* Show Pin icon for pinned booleans (not optimizing) */}
        {type === "boolean" && isPinned && !isOptimizing && (
          <Pin className="h-3 w-3 text-blue-400 opacity-60" />
        )}
        {/* Show Zap icon for optimizing booleans */}
        {type === "boolean" && isOptimizing && (
          <Zap className="h-3 w-3 text-amber-400 opacity-70" />
        )}
        <span className="text-sm">{label}</span>
      </div>

      {/* Value display based on type */}
      {type === "range" &&
        renderRangeValue(value as number | [number, number], isPinned)}
      {type === "number" &&
        renderNumberValue(value as number | [number, number], isPinned)}
      {type === "boolean" && renderBooleanValue(value as boolean, isOptimizing)}
    </div>
  );

  // Wrap with tooltip if description is provided
  if (description) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent aria-label={`${label} description`}>
          <p className="max-w-xs">
            {description}
            {type === "boolean" && isPinned && !isOptimizing && (
              <span className="block mt-1 text-blue-400 text-xs">
                Pinned (Always {value ? "ON" : "OFF"})
              </span>
            )}
            {type === "boolean" && isOptimizing && (
              <span className="block mt-1 text-amber-400 text-xs">
                Optimized (Tested in both On/Off states)
              </span>
            )}
            {(type === "range" || type === "number") && isPinned && (
              <span className="block mt-1 text-amber-400 text-xs">
                Pinned value (not optimized)
              </span>
            )}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}

/**
 * Format a numeric range for display.
 *
 * Collapses a pinned range (min === max) to a single value, otherwise renders
 * "[min, max]". Centralizing this guards against passing a raw tuple to JSX,
 * where React would concatenate the elements into unreadable digits
 * (e.g. [10, 600] -> "10600", [-2.5, 2.5] -> "-2.52.5").
 */
function formatRange(min: number, max: number): string {
  return min === max ? `${min}` : `[${min}, ${max}]`;
}

/**
 * Render a range value [min, max] or single pinned value
 * Handles both tuple [number, number] and single number (for pinned ranges)
 */
function renderRangeValue(value: number | [number, number], isPinned: boolean) {
  // Handle single number (pinned range stored as scalar)
  if (typeof value === "number") {
    return (
      <Badge
        variant="default"
        className="font-mono text-xs bg-amber-500/10 text-amber-400 border-amber-500/20"
      >
        {value}
      </Badge>
    );
  }

  // Defensive check: ensure array has at least 2 elements
  if (!Array.isArray(value) || value.length < 2) {
    return (
      <Badge variant="destructive" className="font-mono text-xs">
        Invalid
      </Badge>
    );
  }

  const [min, max] = value;
  const displayValue = formatRange(min, max);

  return (
    <Badge
      variant={isPinned || min === max ? "default" : "secondary"}
      className={cn(
        "font-mono text-xs",
        (isPinned || min === max) &&
          "bg-amber-500/10 text-amber-400 border-amber-500/20"
      )}
    >
      {displayValue}
    </Badge>
  );
}

/**
 * Render a single numeric value.
 *
 * Defensive against receiving a range tuple (e.g. a range parameter mislabeled
 * as a number): such a value is formatted as a range instead of being handed to
 * JSX directly, where React would concatenate the array (e.g. [10, 600] would
 * render as "10600").
 */
function renderNumberValue(value: number | [number, number], isPinned: boolean) {
  const displayValue = Array.isArray(value)
    ? formatRange(value[0] ?? 0, value[1] ?? 0)
    : value;

  return (
    <Badge
      variant={isPinned ? "default" : "secondary"}
      className={cn(
        "font-mono text-xs",
        isPinned && "bg-amber-500/10 text-amber-400 border-amber-500/20"
      )}
    >
      {displayValue}
    </Badge>
  );
}

/**
 * Render a boolean value with three possible states:
 * - OFF: Gray styling
 * - Optimize: Amber with Zap icon
 * - ON: Emerald/green styling
 */
function renderBooleanValue(value: boolean, isOptimizing: boolean) {
  if (isOptimizing) {
    return (
      <Badge
        variant="default"
        className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/20"
      >
        <Zap className="h-3 w-3 mr-1" />
        Optimize
      </Badge>
    );
  }

  if (value) {
    return (
      <Badge
        variant="default"
        className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
      >
        On
      </Badge>
    );
  }

  return (
    <Badge variant="secondary" className="text-xs">
      Off
    </Badge>
  );
}
